/** Demo trust state for ?mock=1, built from the demo machines. */
import type { AuditVerifyResult, Machine, NodeTrustInfo, TrustState } from '@orgus/protocol';
import { compareVersions } from '@orgus/protocol';

/** The demo hub's minimum agent version: the oldest demo machine is below it. */
const MIN = '0.1.0';

const fp = (seed: string) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let h = 2166136261;
  let out = '';
  for (let i = 0; i < 43; i++) {
    h = Math.imul(h ^ (seed.charCodeAt(i % seed.length) + i), 16777619) >>> 0;
    out += chars[h % 64];
  }
  return `SHA256:${out}`;
};

const hex = (seed: string) => {
  let h = 2166136261;
  let out = '';
  for (let i = 0; i < 64; i++) {
    h = Math.imul(h ^ (seed.charCodeAt(i % seed.length) + i), 16777619) >>> 0;
    out += (h % 16).toString(16);
  }
  return out;
};

export function mockTrust(machines: Machine[]): TrustState {
  const now = Date.now();
  const nodes: NodeTrustInfo[] = machines.map((m, i) => {
    const legacy = i === machines.length - 1 && machines.length > 1;
    return {
      machineId: m.id,
      status: legacy ? 'legacy' : 'key',
      fingerprint: legacy ? null : fp(m.id),
      storage: legacy ? null : 'file',
      registeredAt: legacy ? null : now - (i + 2) * 86_400_000,
      lastVerifiedAt: legacy ? null : now - (i + 1) * 95_000,
      ceilings: i === 0 ? ['session.start:terminal'] : i === 1 ? ['job.run', 'session.start:terminal'] : [],
      belowMinimum: compareVersions(m.agentVersion, MIN) < 0,
      // COMPAT-3: the machine enrolled before keys proved one, which waits for an admin.
      pendingKey: legacy
        ? { fingerprint: fp(`${m.id}-pending`), storage: 'file', firstSeenAt: now - 26 * 60_000, lastSeenAt: now - 4 * 60_000, remote: '100.64.0.12', conflicts: 1, others: [{ fingerprint: fp(`${m.id}-other`), storage: 'file', lastSeenAt: now - 31 * 60_000, remote: '100.64.0.12' }], frozen: false }
        : null,
      // RN-SD3-FORK-1: one identity seen in two places at once.
      quarantine: i === 1 && machines.length > 2 ? { at: now - 7 * 60_000, reason: `${m.name} connected from 192.168.1.40 with connection counter 12, not above the last one seen (14).`, remotes: ['100.64.0.7', '192.168.1.40'] } : null,
    };
  });
  const second = machines[1];
  return {
    frozen: null,
    pausedProjects: [],
    minAgentVersion: MIN,
    requireKeys: false,
    remoteMachines: { allowed: false, warning: null, legacy: false },
    relay: { on: false, link: 'off', since: null, error: null },
    commandKey: { fingerprint: fp('hub-command-key'), createdAt: now - 9 * 86_400_000 },
    nodes,
    rejections: second
      ? [{ at: now - 42 * 60_000, machineId: second.id, what: 'Command dropped by the machine', reason: 'Command had expired (job.run).' }]
      : [],
    audit: { head: hex('audit-head'), chained: 214 },
  };
}

export async function mockVerify(s: TrustState | null): Promise<AuditVerifyResult> {
  await new Promise((r) => setTimeout(r, 400));
  return { ok: true, entries: 226, legacy: 12, chained: s?.audit.chained ?? 214, head: s?.audit.head ?? null, anchor: { head: s?.audit.head ?? '', count: 226, matches: true } };
}
